import { Injectable, inject } from '@angular/core';
import { App, AppState } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fromEventPattern, merge, timer } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { StatsUpdateLogQueryService } from 'src/app/shared/services/stats-update-log-query.service';
import * as StatsActionActions from '../stats-action/stats-action.actions';
import * as StatsConnectActions from '../stats-connect/stats-connect.actions';
import { StatsUpdateLogActions } from '../stats-update-log/stats-update-log.actions';

const heartbeatIntervalMs = 2 * 60 * 1000 + 15 * 1000;

@Injectable()
export class StatsHeartbeatEffects {
    private readonly actions$ = inject(Actions);
    private readonly statsUpdateLogQueryService = inject(StatsUpdateLogQueryService);
    private lastStatsActivityAt = 0;

    private readonly foreground$ = fromEventPattern<AppState>(
        handler => App.addListener('appStateChange', handler),
        (_handler, listenerPromise: Promise<PluginListenerHandle>) => {
            void listenerPromise.then(listener => listener.remove());
        },
    ).pipe(
        filter(state => state.isActive),
    );

    onStatsActivity$ = createEffect(() => merge(
        this.actions$.pipe(
            ofType(StatsUpdateLogActions.queue),
        ),
        this.actions$.pipe(
            ofType(StatsActionActions.load, StatsConnectActions.load),
        ),
    ).pipe(
        tap(() => this.lastStatsActivityAt = Date.now()),
        switchMap(() => timer(heartbeatIntervalMs).pipe(
            filter(() => this.isHeartbeatDue()),
            map(() => this.createHeartbeatAction()),
        )),
    ));

    onForeground$ = createEffect(() => this.foreground$.pipe(
        filter(() => this.isHeartbeatDue()),
        map(() => this.createHeartbeatAction()),
    ));

    private isHeartbeatDue(): boolean {
        return this.lastStatsActivityAt > 0 &&
            Date.now() - this.lastStatsActivityAt >= heartbeatIntervalMs;
    }

    private createHeartbeatAction(): ReturnType<typeof StatsUpdateLogActions.queue> {
        this.lastStatsActivityAt = Date.now();
        return StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_CHECK', 'HEARTBEAT', {
                details: { heartbeatIntervalMs },
                updateCheckType: 'BACKGROUND',
            }),
        });
    }
}
